/**
 * Image Slider — auto-width Splide gallery that bleeds past the container, with
 * header arrows and a click-to-open lightbox.
 */
export function imageSlider(Splide) {
  const sliders = document.querySelectorAll('[data-image-slider]');
  if (!sliders.length) return;

  ensureLightbox();

  sliders.forEach((el) => {
    const splide = new Splide(el, {
      type: 'slide',
      autoWidth: true,
      gap: '24px',
      arrows: false,
      pagination: false,
      drag: true,
      speed: 600,
      easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
      // Start at the container's left edge, bleed to the right viewport edge.
      padding: {
        left: 'calc(max(0px, (100vw - 1418px) / 2) + var(--slider-gutter))',
        right: 0,
      },
    });

    splide.mount();

    const root = el.closest('.image-slider') || el.parentElement;
    const prev = root?.querySelector('[data-slider-prev]');
    const next = root?.querySelector('[data-slider-next]');
    prev?.addEventListener('click', () => splide.go('<'));
    next?.addEventListener('click', () => splide.go('>'));

    const update = () => {
      const end = splide.Components.Controller.getEnd();
      if (prev) prev.disabled = splide.index <= 0;
      if (next) next.disabled = splide.index >= end;
    };
    splide.on('mounted move updated refresh', update);
    update();

    const slides = [...el.querySelectorAll('.splide__slide')];
    const widths = new Map(slides.map((slide) => [slide, slide.getBoundingClientRect().width]));
    let moving = false;
    let dragging = false;
    let needsRefresh = false;
    let refreshFrame = 0;

    // Fixed-size screenshots don't change the layout when they load. Refreshing
    // Splide anyway destroys its slides and interrupts an in-flight transition.
    // Intrinsically sized image galleries still need a refresh, after movement ends.
    const refreshIfIdle = () => {
      if (!needsRefresh || moving || dragging || refreshFrame) return;
      refreshFrame = requestAnimationFrame(() => {
        refreshFrame = 0;
        if (moving || dragging) return;
        needsRefresh = false;
        splide.refresh();
        slides.forEach((slide) => widths.set(slide, slide.getBoundingClientRect().width));
      });
    };

    // Fetch and decode only the visible range and its neighbours. The rest of
    // the gallery remains lazy, including when this section is far below the fold.
    const prepared = new WeakSet();
    const prepareImages = (index = splide.index) => {
      const slideWidth = slides[index]?.getBoundingClientRect().width || el.clientWidth;
      const visible = Math.ceil(el.clientWidth / Math.max(slideWidth, 1));
      slides.slice(Math.max(0, index - 1), index + visible + 2).forEach((slide) => {
        slide.querySelectorAll('img').forEach((img) => {
          if (prepared.has(img)) return;
          prepared.add(img);
          img.loading = 'eager';
          img.decode?.().catch(() => {});
        });
      });
    };
    splide.on('move', (index) => { moving = true; prepareImages(index); });
    splide.on('moved', () => { moving = false; refreshIfIdle(); });
    splide.on('drag', () => { dragging = true; prepareImages(); });
    splide.on('dragged', () => { dragging = false; refreshIfIdle(); });
    splide.on('resized', () => {
      slides.forEach((slide) => widths.set(slide, slide.getBoundingClientRect().width));
    });

    el.querySelectorAll('img').forEach((img) => {
      if (!img.complete) {
        img.addEventListener('load', () => {
          const slide = img.closest('.splide__slide');
          if (slide && Math.abs(slide.getBoundingClientRect().width - widths.get(slide)) > 0.5) {
            needsRefresh = true;
            refreshIfIdle();
          }
        }, { once: true });
      }
    });

    const nearby = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        prepareImages();
        nearby.disconnect();
      }
    }, { rootMargin: '400px 0px' });
    nearby.observe(el);
    splide.on('destroy', () => { nearby.disconnect(); cancelAnimationFrame(refreshFrame); });
  });
}

let lightboxEl = null;

export function ensureLightbox() {
  if (lightboxEl) return;

  // Localised close label, provided by the block (falls back to English)
  const closeLabel =
    document.querySelector('[data-image-slider]')?.dataset.lightboxClose || 'Close';

  lightboxEl = document.createElement('div');
  lightboxEl.className = 'image-lightbox';
  lightboxEl.hidden = true;
  lightboxEl.innerHTML = `
    <button type="button" class="image-lightbox__close" aria-label="${closeLabel}">&times;</button>
    <figure class="image-lightbox__figure">
      <img class="image-lightbox__img" alt="" />
      <figcaption class="image-lightbox__desc"></figcaption>
    </figure>`;
  document.body.appendChild(lightboxEl);

  const close = () => {
    lightboxEl.hidden = true;
    document.body.style.overflow = '';
  };

  lightboxEl.addEventListener('click', (e) => {
    if (e.target === lightboxEl || e.target.closest('.image-lightbox__close')) close();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !lightboxEl.hidden) close();
  });

  // Delegated: open the lightbox for any slide trigger
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-lightbox]');
    if (!btn) return;
    e.preventDefault();

    const img = btn.querySelector('img');
    const src = img?.currentSrc || img?.src || '';
    const desc = btn.getAttribute('data-desc') || '';

    const lbImg = lightboxEl.querySelector('.image-lightbox__img');
    const lbDesc = lightboxEl.querySelector('.image-lightbox__desc');
    lbImg.src = src;
    lbDesc.textContent = desc;
    lbDesc.hidden = !desc;

    lightboxEl.hidden = false;
    document.body.style.overflow = 'hidden';
  });
}
